import React from 'react';
import {
  MdOutlineAssignmentReturn,
  MdOutlineDeleteOutline,
  MdOutlineFactCheck,
  MdOutlineMoveToInbox,
  MdOutlineOutbox,
  MdOutlinePayments,
  MdOutlinePendingActions,
  MdOutlineSell,
  MdOutlineShoppingCart,
  MdOutlineSwapHoriz,
} from 'react-icons/md';
import { TbTruckReturn } from 'react-icons/tb';

/** Loại giao dịch chuẩn hoá dùng chung cho toàn hệ thống. */
export type TransactionTypeKey =
  | 'hold'
  | 'purchased'
  | 'received'
  | 'deducted'
  | 'sold'
  | 'counting'
  | 'return_supplier'
  | 'return_order'
  | 'payment'
  | 'dispose'
  | 'all';

export interface TransactionTypeMeta {
  key: TransactionTypeKey;
  label: string;
  icon: React.ReactNode;
  /** Màu antd cho biến thể tag nhạt (soft). */
  color: string;
  /** Màu antd cho biến thể tag đậm (solid). */
  solidColor: string;
  /** Các mã/nhãn khác nhau từ backend cùng trỏ về loại giao dịch này. */
  aliases: string[];
}

const META: readonly TransactionTypeMeta[] = [
  {
    key: 'all',
    label: 'Tất cả giao dịch',
    icon: <MdOutlineSwapHoriz />,
    color: 'default',
    solidColor: 'default',
    aliases: ['all', ''],
  },
  {
    key: 'hold',
    label: 'Tạm giữ hàng',
    icon: <MdOutlinePendingActions />,
    color: 'gold',
    solidColor: 'gold-inverse',
    aliases: ['order', 'hold'],
  },
  {
    key: 'purchased',
    label: 'Mua hàng',
    icon: <MdOutlineShoppingCart />,
    color: 'purple',
    solidColor: 'purple-inverse',
    aliases: ['purchased', 'purchase'],
  },
  {
    key: 'received',
    label: 'Nhập kho',
    icon: <MdOutlineMoveToInbox />,
    color: 'blue',
    solidColor: 'blue-inverse',
    aliases: ['received', 'goods_receive', 'Movement', 'Nhập kho'],
  },
  {
    key: 'deducted',
    label: 'Xuất kho',
    icon: <MdOutlineOutbox />,
    color: 'orange',
    solidColor: 'orange-inverse',
    aliases: ['deducted', 'goods_issue', 'Xuất kho'],
  },
  {
    key: 'sold',
    label: 'Bán hàng',
    icon: <MdOutlineSell />,
    color: 'green',
    solidColor: 'green-inverse',
    aliases: ['sold', 'Order', 'Bán hàng'],
  },
  {
    key: 'counting',
    label: 'Kiểm kho',
    icon: <MdOutlineFactCheck />,
    color: 'geekblue',
    solidColor: 'geekblue-inverse',
    aliases: ['counting', 'stock_counting', 'Kiểm kho'],
  },
  {
    key: 'return_supplier',
    label: 'Trả hàng NCC',
    icon: <TbTruckReturn />,
    color: 'volcano',
    solidColor: 'volcano-inverse',
    aliases: ['return_supplier', 'Trả hàng NCC'],
  },
  {
    key: 'return_order',
    label: 'Trả hàng',
    icon: <MdOutlineAssignmentReturn />,
    color: 'magenta',
    solidColor: 'magenta-inverse',
    aliases: ['Return-Order', 'return', 'Trả hàng'],
  },
  {
    key: 'payment',
    label: 'Thanh toán',
    icon: <MdOutlinePayments />,
    color: 'cyan',
    solidColor: 'cyan-inverse',
    aliases: ['Ledger', 'payment', 'Thanh toán'],
  },
  {
    key: 'dispose',
    label: 'Xuất huỷ sản phẩm',
    icon: <MdOutlineDeleteOutline />,
    color: 'red',
    solidColor: 'red-inverse',
    aliases: ['dispose', 'destroy', 'cancel', 'Xuất huỷ sản phẩm', 'Huỷ'],
  },
];

const FALLBACK_META: TransactionTypeMeta = {
  key: 'all',
  label: '',
  icon: <MdOutlineSwapHoriz />,
  color: 'default',
  solidColor: 'default',
  aliases: [],
};

const allAliases = (meta: TransactionTypeMeta): string[] => [meta.key, meta.label, ...meta.aliases];

/**
 * Tra cứu phân biệt hoa thường — bắt buộc vì backend dùng cả `order` (tạm giữ hàng, `inventory_type`)
 * lẫn `Order` (bán hàng, `ledgerable_type`).
 */
const EXACT_INDEX: ReadonlyMap<string, TransactionTypeMeta> = new Map(
  META.flatMap((meta) => allAliases(meta).map((alias) => [alias, meta] as const)),
);

/** Tra cứu dự phòng khi sai hoa thường; alias khai báo trước được ưu tiên. */
const LOWER_INDEX: ReadonlyMap<string, TransactionTypeMeta> = META.reduce((index, meta) => {
  allAliases(meta).forEach((alias) => {
    const key = alias.toLowerCase();
    if (!index.has(key)) index.set(key, meta);
  });
  return index;
}, new Map<string, TransactionTypeMeta>());

/** Trả về metadata của một loại giao dịch; fallback giữ nguyên nhãn gốc nếu không nhận diện được. */
export const transactionTypeMeta = (value?: string | null): TransactionTypeMeta => {
  if (!value) return FALLBACK_META;

  const normalized = String(value).trim();
  return (
    EXACT_INDEX.get(normalized) ??
    LOWER_INDEX.get(normalized.toLowerCase()) ?? { ...FALLBACK_META, label: normalized }
  );
};

/** Nhãn tiếng Việt của loại giao dịch. */
export const transactionTypeName = (value?: string | null): string => transactionTypeMeta(value).label;

export interface TransactionTypeOption {
  value: string;
  label: string;
  icon: React.ReactNode;
}

/** Mã giao dịch, hoặc mã kèm nhãn tuỳ biến cho màn hình cụ thể. */
export type TransactionTypeInput = string | { value: string; label?: string };

/**
 * Tạo danh sách option cho Select "Loại giao dịch".
 * @param values mã giao dịch theo đúng thứ tự muốn hiển thị (mã gửi lên API được giữ nguyên).
 */
export const transactionTypeOptions = (values: readonly TransactionTypeInput[]): TransactionTypeOption[] =>
  values.map((entry) => {
    const { value, label } = typeof entry === 'string' ? { value: entry, label: undefined } : entry;
    const meta = transactionTypeMeta(value);
    return { value, label: label ?? meta.label, icon: meta.icon };
  });

export interface TransactionTypeSelectOption {
  value: string;
  label: React.ReactNode;
  /** Nhãn dạng chuỗi, dùng cho tìm kiếm/lọc trong Select. */
  name: string;
}

/**
 * Option cho Select "Loại giao dịch" với icon đi kèm.
 * Dùng `name` khi cần filterOption vì `label` là ReactNode.
 */
export const transactionTypeSelectOptions = (
  values: readonly TransactionTypeInput[],
): TransactionTypeSelectOption[] =>
  transactionTypeOptions(values).map(({ value, label, icon }) => ({
    value,
    name: label,
    label: (
      <span className="transaction-type-option">
        <span className="transaction-type-option__icon">{icon}</span>
        <span className="transaction-type-option__label">{label}</span>
      </span>
    ),
  }));
